import CustomLoadingButton from 'Components/CustomLoadingButton'
import {
  ReportGenerationStatus,
  type ReportGenerationStatusShape,
  type ReportResponse
} from 'Services/SalesManagement/Features/Report/reportSlice'
import { REPORT_DOWNLOAD_URL_ALLOWED_HOST_NAMES } from 'Util/constants'
import SnackbarUtils from 'Util/Snackbar'
import DownloadReportIcon from '@mui/icons-material/CloudDownload'
import Box from '@mui/material/Box'
import { useEffect, useState } from 'react'
import { t } from 'ttag'

const handleReportDownload = (reportUrl: string) => {
  // Validate the download URL before proceeding

  let url: URL
  try {
    url = new URL(reportUrl)
  } catch {
    SnackbarUtils.error(t`The report download URL is invalid.`)
    return
  }

  if (!REPORT_DOWNLOAD_URL_ALLOWED_HOST_NAMES.includes(url.hostname)) {
    SnackbarUtils.error(t`The report download URL is invalid.`)
    return
  }

  // Trigger download using window.open
  window.open(reportUrl, '_blank')
}

interface ReportDownloadProps {
  singleReport: ReportResponse | null
  reportGenerationStatus: ReportGenerationStatusShape | null
  fetchReport: (reportName: string) => Promise<ReportResponse>
  isLoading: boolean
}

export default function ReportDownload({
  singleReport,
  reportGenerationStatus,
  fetchReport,
  isLoading
}: Readonly<ReportDownloadProps>) {
  const [reportName, setReportName] = useState<string | null>(null)

  /**
   * Effect to handle report fetching when the report generation status changes to SUCCESS.
   */
  useEffect(() => {
    // Only fetch the report if it has not been fetched yet
    if (!singleReport) {
      // Check if the report generation was successful
      if (reportGenerationStatus?.status === ReportGenerationStatus.SUCCESS) {
        // If a report name is available in the report generation status, fetch the report
        if (reportGenerationStatus?.info?.report_name) {
          setReportName(reportGenerationStatus.info.report_name)
        }
      }
    }
  }, [reportGenerationStatus?.status, singleReport])

  const getReport = (reportName: string | null) => {
    if (!reportName) {
      return
    }

    fetchReport(reportName)
      .then(reportResponse => {
        handleReportDownload(reportResponse.download_url)
      })
      .catch(() => {
        SnackbarUtils.error(t`There was an error fetching the report.`)
      })
  }

  return (
    <Box sx={{ my: 4, display: 'flex', justifyContent: 'end' }}>
      {reportName != null && (
        <CustomLoadingButton
          isLoading={isLoading}
          onClick={() => getReport(reportName)}
          icon={<DownloadReportIcon />}
          iconPosition="start"
        >
          {t`Download report`}
        </CustomLoadingButton>
      )}
    </Box>
  )
}
